// FancyAudioPlayer.jsx
import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

export default function FancyAudioPlayer({ src, title }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const speeds = [0.5, 1, 1.5, 2];
  const [speedIdx, setSpeedIdx] = useState(1);

  // formata em mm:ss
  const format = sec => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    const aud = audioRef.current;
    if (!aud) return;
    const onLoaded = () => setDuration(aud.duration);
    const onTime = () => setCurrentTime(aud.currentTime);

    aud.addEventListener('loadedmetadata', onLoaded);
    aud.addEventListener('timeupdate', onTime);
    return () => {
      aud.removeEventListener('loadedmetadata', onLoaded);
      aud.removeEventListener('timeupdate', onTime);
    };
  }, []);

  const togglePlay = () => {
    const aud = audioRef.current;
    if (!aud) return;
    if (isPlaying) aud.pause();
    else aud.play();
    setIsPlaying(!isPlaying);
  };

  const onSeek = e => {
    const t = Number(e.target.value);
    audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const onVolumeChange = e => {
    const v = Number(e.target.value);
    setVolume(v);
    audioRef.current.volume = v;
    if (v > 0 && isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    const aud = audioRef.current;
    aud.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const changeSpeed = () => {
    const next = (speedIdx + 1) % speeds.length;
    setSpeedIdx(next);
    audioRef.current.playbackRate = speeds[next];
  };

  return (
    <div className="w-full bg-gray-50 dark:bg-gray-700 p-4 rounded-md shadow-sm space-y-2">
      {title && (
        <div className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-1">
          {title}
        </div>
      )}

      <div className="flex items-center space-x-3">
        <button
          onClick={togglePlay}
          className="p-2 bg-white dark:bg-gray-800 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5 text-blue-600" />
          ) : (
            <Play className="w-5 h-5 text-blue-600" />
          )}
        </button>

        <div className="flex-1">
          <input
            type="range"
            min={0}
            max={duration || 0}
            value={currentTime}
            onChange={onSeek}
            className="w-full h-1.5 bg-gray-200 rounded-lg cursor-pointer dark:bg-gray-600"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>{format(currentTime)}</span>
            <span>{format(duration)}</span>
          </div>
        </div>

        <button
          onClick={toggleMute}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition"
        >
          {isMuted || volume === 0 ? (
            <VolumeX className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          ) : (
            <Volume2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          )}
        </button>

        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={isMuted ? 0 : volume}
          onChange={onVolumeChange}
          className="w-20 h-1.5 bg-gray-200 rounded-lg cursor-pointer dark:bg-gray-600"
        />

        <button
          onClick={changeSpeed}
          className="px-2 py-1 bg-white dark:bg-gray-800 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-sm text-gray-600 dark:text-gray-300"
        >
          {speeds[speedIdx]}×
        </button>
      </div>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onEnded={() => setIsPlaying(false)}
      />
    </div>
  );
}
